import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';

export function FaturaDialog({ 
  open, 
  onOpenChange, 
  form, 
  setForm, 
  onSubmit, 
  onReset,
  clientes = [],
  contratos = [],
  onAddItem,
  onUpdateItem,
  onRemoveItem
}) {
  const handleClose = () => {
    onOpenChange(false);
    onReset?.();
  };

  const total = form.itens.reduce((acc, item) => acc + (item.quantidade || 0) * (item.valor_unitario || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          <DialogHeader>
            <DialogTitle>Nova Fatura</DialogTitle>
            <DialogDescription>Crie uma nova fatura para cobrança</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cliente *</Label>
                <Select 
                  value={form.cliente_id || undefined} 
                  onValueChange={(v) => setForm(prev => ({ ...prev, cliente_id: v }))}
                >
                  <SelectTrigger data-testid="select-cliente-fatura">
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clientes.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contrato (opcional)</Label>
                <Select 
                  value={form.contrato_id || 'none'} 
                  onValueChange={(v) => setForm(prev => ({ ...prev, contrato_id: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {contratos.filter(c => c.status === 'ativo').map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.numero_contrato} - {c.cliente_nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Período Início</Label>
                <Input 
                  type="date"
                  value={form.periodo_inicio}
                  onChange={(e) => setForm(prev => ({ ...prev, periodo_inicio: e.target.value }))}
                />
              </div>
              <div>
                <Label>Período Fim</Label>
                <Input 
                  type="date"
                  value={form.periodo_fim}
                  onChange={(e) => setForm(prev => ({ ...prev, periodo_fim: e.target.value }))}
                />
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Itens da Fatura *</Label>
                <Button type="button" size="sm" variant="outline" onClick={onAddItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar Item
                </Button>
              </div>
              
              {form.itens.length === 0 && (
                <div className="text-center py-4 text-slate-500 text-sm border-2 border-dashed rounded-lg">
                  Clique em "Adicionar Item" para incluir serviços na fatura
                </div>
              )}
              
              {form.itens.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Descrição</Label>
                    <Input 
                      value={item.descricao}
                      onChange={(e) => onUpdateItem(index, 'descricao', e.target.value)}
                      placeholder="Descrição do serviço"
                    />
                  </div>
                  <div className="w-20">
                    <Label className="text-xs">Qtd</Label>
                    <Input 
                      type="number"
                      value={item.quantidade}
                      onChange={(e) => onUpdateItem(index, 'quantidade', parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs">Valor Unit.</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={item.valor_unitario}
                      onChange={(e) => onUpdateItem(index, 'valor_unitario', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="w-28">
                    <Label className="text-xs">Subtotal</Label>
                    <Input 
                      type="text"
                      readOnly
                      value={`R$ ${((item.quantidade || 0) * (item.valor_unitario || 0)).toFixed(2)}`}
                      className="bg-slate-50"
                    />
                  </div>
                  <Button type="button" size="icon" variant="ghost" onClick={() => onRemoveItem(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
              
              {form.itens.length > 0 && (
                <div className="flex justify-end mt-2 pt-2 border-t">
                  <div className="text-right">
                    <span className="text-sm text-slate-500">Total da Fatura: </span>
                    <span className="text-lg font-bold">
                      R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <Label>Observações</Label>
              <Textarea 
                value={form.observacoes}
                onChange={(e) => setForm(prev => ({ ...prev, observacoes: e.target.value }))}
                placeholder="Observações da fatura..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="btn-salvar-fatura">
              Criar Fatura
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
